import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import toast from '../../../components/ui/Toast';
import analyticsService from '../../../services/analyticsService';

const DowngradeAssistant = ({ limitStatus, agents, onClose, onConfirm }) => {
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [startTime] = useState(Date.now());
  const [retryCount, setRetryCount] = useState(0);
  const agentsToDeactivate = limitStatus?.agents_to_deactivate || 0;
  const activeAgents = agents?.filter(a => a?.isActive) || [];

  // Track when downgrade flow is initiated
  useEffect(() => {
    analyticsService?.trackDowngradeFlow(
      'initiated',
      agentsToDeactivate,
      0,
      0
    );
  }, []);

  const formatGracePeriod = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  const gracePeriodRemaining = formatGracePeriod(limitStatus?.grace_period_end);

  const toggleAgentSelection = (agentId) => {
    setSelectedAgents(prev => {
      const newSelection = prev?.includes(agentId)
        ? prev?.filter(id => id !== agentId)
        : [...prev, agentId];
      
      // Track agent selection progress
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      analyticsService?.trackDowngradeFlow(
        'agent_selection',
        agentsToDeactivate,
        newSelection?.length,
        timeSpent
      );
      
      return newSelection;
    });
  };

  const isSelectionComplete = selectedAgents?.length >= agentsToDeactivate;

  const getImpactColor = (impact) => {
    if (impact?.startsWith('High')) return 'text-red-500';
    if (impact?.startsWith('Medium')) return 'text-amber-500';
    return 'text-green-500';
  };

  const getImpactBg = (impact) => {
    if (impact?.startsWith('High')) return 'bg-red-500/10 border-red-500/20';
    if (impact?.startsWith('Medium')) return 'bg-amber-500/10 border-amber-500/20';
    return 'bg-green-500/10 border-green-500/20';
  };

  const handleConfirmClick = () => {
    if (isSelectionComplete) {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      
      // Track confirmation step
      analyticsService?.trackDowngradeFlow(
        'confirmation',
        agentsToDeactivate,
        selectedAgents?.length,
        timeSpent
      );
      
      setShowConfirmation(true);
    }
  };

  const handleConfirmDowngrade = async () => {
    setShowConfirmation(false);
    setIsProcessing(true);
    const currentRetry = retryCount;
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    
    toast?.loading('Processing downgrade...');
    
    try {
      // Simulate API call - replace with actual implementation
      if (onConfirm) {
        await onConfirm(selectedAgents);
      }
      
      toast?.dismiss();
      setShowSuccess(true);
      
      // Track successful downgrade completion
      analyticsService?.trackDowngradeFlow(
        'completed',
        agentsToDeactivate,
        selectedAgents?.length,
        timeSpent
      );
      
      // Track each agent deactivation
      selectedAgents?.forEach(agentId => {
        const agent = agents?.find(a => a?.id === agentId);
        if (agent) {
          analyticsService?.trackAgentToggle(
            agent?.id,
            agent?.name,
            false,
            'downgrade_required'
          );
        }
      });
      
      // If this was a retry, track successful recovery
      if (currentRetry > 0) {
        analyticsService?.trackErrorRecovery(
          'downgrade_failed',
          'Downgrade succeeded after retry',
          currentRetry,
          true,
          `${selectedAgents?.length} agents`
        );
        setRetryCount(0);
      }
      
      toast?.success('Downgrade completed successfully', {
        description: `${selectedAgents?.length} agent${selectedAgents?.length > 1 ? 's' : ''} deactivated`,
        duration: 4000
      });
    } catch (error) {
      const newRetryCount = currentRetry + 1;
      setRetryCount(newRetryCount);
      
      toast?.dismiss();
      setErrorDetails({
        message: error?.message || 'Failed to process downgrade',
        failedAgents: selectedAgents?.slice(0, 2),
        totalFailed: selectedAgents?.length
      });
      setShowError(true);
      
      // Track downgrade error
      analyticsService?.trackErrorRecovery(
        'downgrade_failed',
        error?.message || 'Failed to process downgrade',
        newRetryCount,
        false,
        `${selectedAgents?.length} agents`
      );
      
      // Track friction point
      analyticsService?.trackFrictionPoint(
        'downgrade_flow',
        'downgrade_error',
        4,
        { agents_count: selectedAgents?.length, retry_count: newRetryCount }
      );
      
      toast?.error('Downgrade failed', {
        description: 'Some agents could not be deactivated',
        action: {
          label: 'View Details',
          onClick: () => setShowError(true)
        }
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetry = async () => {
    setShowError(false);
    await handleConfirmDowngrade();
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    onClose();
  };
  
  const handleClose = () => {
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    
    // Track abandoned flow
    analyticsService?.trackDowngradeFlow(
      'abandoned',
      agentsToDeactivate,
      selectedAgents?.length,
      timeSpent
    );
    
    // Track friction if user spent significant time but didn't complete
    if (timeSpent > 60 && selectedAgents?.length < agentsToDeactivate) {
      analyticsService?.trackFrictionPoint(
        'downgrade_flow',
        'abandoned_after_hesitation',
        3,
        { time_spent: timeSpent, selection_progress: selectedAgents?.length / agentsToDeactivate }
      );
    }
    
    onClose();
  };

  return (
    <div className="bg-gradient-to-br from-orange-500/10 to-red-500/5 border border-orange-500/20 rounded-xl p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
            <Icon name="AlertTriangle" size={24} className="text-orange-500" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-1 font-heading">
              Downgrade Assistant
            </h2>
            <p className="text-sm text-muted-foreground">
              Select {agentsToDeactivate} agent{agentsToDeactivate > 1 ? 's' : ''} to deactivate to comply with your new plan
            </p>
          </div>
        </div>
        <button
          onClick={handleClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <Icon name="X" size={20} />
        </button>
      </div>
      {/* Grace Period Warning */}
      <div className="flex items-start gap-3 p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
        <Icon name="Clock" size={20} className="text-orange-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-orange-500 mb-1">
            Grace Period: {gracePeriodRemaining} remaining
          </p>
          <p className="text-xs text-orange-500/80">
            You must deactivate {agentsToDeactivate} agent{agentsToDeactivate > 1 ? 's' : ''} before the grace period ends, or your account will be restricted.
          </p>
        </div>
      </div>
      {/* Selection Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Selected for deactivation:</span>
          <span className="font-semibold text-foreground">
            {selectedAgents?.length} / {agentsToDeactivate}
          </span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              isSelectionComplete ? 'bg-green-500' : 'bg-orange-500'
            }`}
            style={{ width: `${Math.min((selectedAgents?.length / agentsToDeactivate) * 100, 100)}%` }}
          />
        </div>
      </div>
      {/* Agent Selection List */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Active Agents</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
          {activeAgents?.map((agent) => {
            const isSelected = selectedAgents?.includes(agent?.id);
            return (
              <div
                key={agent?.id}
                onClick={() => toggleAgentSelection(agent?.id)}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all duration-200
                  ${isSelected 
                    ? 'bg-orange-500/10 border-orange-500/30' : 'bg-card border-border hover:border-primary/50'
                  }
                `}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <div
                    className={`
                      flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                      ${isSelected 
                        ? 'bg-orange-500 border-orange-500' : 'border-muted-foreground'
                      }
                    `}
                  >
                    {isSelected && <Icon name="Check" size={14} className="text-white" />}
                  </div>

                  {/* Agent Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name={agent?.icon} size={16} className="text-foreground" />
                      <h4 className="text-sm font-semibold text-foreground">
                        {agent?.name}
                      </h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Last used: {agent?.lastUsed ? new Date(agent?.lastUsed)?.toLocaleDateString() : 'Never'}
                    </p>
                    
                    {/* Usage Impact */}
                    <div className={`inline-flex items-start gap-1 px-2 py-1 rounded text-xs border ${getImpactBg(agent?.usageImpact)}`}>
                      <Icon name="TrendingDown" size={12} className={`flex-shrink-0 mt-0.5 ${getImpactColor(agent?.usageImpact)}`} />
                      <span className={getImpactColor(agent?.usageImpact)}>
                        {agent?.usageImpact}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Smart Recommendations */}
      <div className="p-4 bg-blue-500/5 border border-blue-500/10 rounded-lg">
        <div className="flex items-start gap-2">
          <Icon name="Lightbulb" size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-blue-500 mb-1">Recommendation</p>
            <p className="text-xs text-blue-500/80">
              Consider deactivating agents with "Low" impact first to minimize disruption to your workflow.
            </p>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          size="md"
          fullWidth
          onClick={onClose}
          disabled={isProcessing}
        >
          I'll Decide Later
        </Button>
        <Button
          variant="default"
          size="md"
          fullWidth
          disabled={!isSelectionComplete || isProcessing}
          iconName="Check"
          iconPosition="right"
          onClick={handleConfirmClick}
        >
          {isProcessing ? 'Processing...' : 'Confirm Selection'}
        </Button>
      </div>
      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        type="warning"
        title="Confirm Downgrade"
        description={`You're about to deactivate ${selectedAgents?.length} agent${selectedAgents?.length > 1 ? 's' : ''}. This action will help you comply with your new subscription plan.`}
        icon="AlertTriangle"
        actions={
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => setShowConfirmation(false)}
              disabled={isProcessing}
            >
              Go Back
            </Button>
            <Button
              variant="destructive"
              size="sm"
              fullWidth
              onClick={handleConfirmDowngrade}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Confirm Downgrade'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="p-3 bg-amber-500/5 border border-amber-500/10 rounded-lg">
            <p className="text-xs font-medium text-amber-500 mb-2">Agents to be deactivated:</p>
            <ul className="space-y-1">
              {selectedAgents?.map(agentId => {
                const agent = agents?.find(a => a?.id === agentId);
                return (
                  <li key={agentId} className="text-xs text-amber-500/80 flex items-center gap-2">
                    <Icon name={agent?.icon} size={12} />
                    {agent?.name}
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="p-3 bg-blue-500/5 border border-blue-500/10 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-500">
                You can reactivate these agents anytime by upgrading your plan.
              </p>
            </div>
          </div>
        </div>
      </Modal>
      {/* Success Modal */}
      <Modal
        isOpen={showSuccess}
        onClose={handleSuccessClose}
        type="success"
        title="Downgrade Completed"
        description="Your subscription has been successfully adjusted and agents have been deactivated."
        icon="CheckCircle"
        actions={
          <Button
            variant="default"
            size="md"
            fullWidth
            onClick={handleSuccessClose}
          >
            Done
          </Button>
        }
      >
        <div className="space-y-3">
          <div className="p-3 bg-green-500/5 border border-green-500/10 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-green-500 mb-1">What's Next?</p>
                <ul className="text-xs text-green-500/80 space-y-1">
                  <li>• Your account is now compliant with your plan</li>
                  <li>• Deactivated agents can be reactivated anytime</li>
                  <li>• Consider upgrading to access more agents</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      {/* Error Modal */}
      <Modal
        isOpen={showError}
        onClose={() => setShowError(false)}
        type="error"
        title="Downgrade Failed"
        description={errorDetails?.message || 'Unable to complete the downgrade process'}
        icon="XCircle"
        actions={
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => setShowError(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              fullWidth
              onClick={handleRetry}
              iconName="RefreshCw"
              iconPosition="left"
              disabled={isProcessing}
            >
              Retry
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="p-3 bg-red-500/5 border border-red-500/10 rounded-lg">
            <p className="text-xs font-medium text-red-500 mb-2">Failed Operations:</p>
            <ul className="space-y-1">
              {errorDetails?.failedAgents?.map(agentId => {
                const agent = agents?.find(a => a?.id === agentId);
                return (
                  <li key={agentId} className="text-xs text-red-500/80 flex items-center gap-2">
                    <Icon name="XCircle" size={12} />
                    {agent?.name} - Could not deactivate
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="p-3 bg-blue-500/5 border border-blue-500/10 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-blue-500 mb-1">Troubleshooting Tips:</p>
                <ul className="text-xs text-blue-500/80 space-y-1">
                  <li>• Check your internet connection</li>
                  <li>• Ensure agents aren&apos;t processing tasks</li>
                  <li>• Try again in a few moments</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DowngradeAssistant;